/**
 * Contextual opener copy for the embedded panel.
 *
 * The loader passes the host page's identity through as `?ctx=<key>`; this
 * module turns that key into the sub-copy, composer placeholder, and starter
 * prompts the panel opens with. One place to edit, because this is
 * conversion copy on paid traffic — an owner decision, not an engineering
 * one.
 *
 * The greeting itself is NOT part of this registry — see `EMBED_GREETING`
 * below. The old Turn 21c "greeting must match the teaser verbatim" rule is
 * retired — a single generic greeting can't drift from a teaser it no longer
 * has to match.
 *
 * The teaser bubble's copy lives here too (since 2026-09-02): the panel sends
 * `teaser` to the loader on its `ready` message, and the loader renders it
 * unless the host tag overrides a field with `data-teaser-*`. So a copy
 * change here ships with this app — no loader release, no host-page edit.
 * Only the host tag knows the page's DOM, which is why `anchor` can still be
 * overridden there.
 *
 * Fallback rule: when the context can't be resolved (deep link, unknown
 * route, teaser suppressed) we open with the same generic greeting and let
 * the sub-copy carry the offer. A confident question about a course we
 * cannot actually name reads worse than no question at all.
 */

/** Icon keys — resolved to SVG at the render layer so this module stays pure. */
export const EMBED_PROMPT_ICON = {
  CALENDAR: 'calendar',
  INFO: 'info',
  PRICE: 'price',
} as const;
export type EmbedPromptIcon =
  (typeof EMBED_PROMPT_ICON)[keyof typeof EMBED_PROMPT_ICON];

export interface EmbedPrompt {
  /** Label shown on the row. */
  label: string;
  /** Text actually sent to Sara — may differ from the label. */
  prompt: string;
  icon: EmbedPromptIcon;
}

/**
 * Copy for the launcher-side teaser bubble, rendered by the loader on the
 * HOST page (not inside the iframe). Every field is overridable per tag via
 * `data-teaser-title` / `-body` / `-anchor`.
 */
export interface EmbedTeaser {
  /** Headline. Empty string = no teaser for this context. */
  title: string;
  body: string;
  /**
   * CSS selector on the host page whose scroll-past arms the teaser early
   * (it also fires on dwell regardless). A guess about someone else's DOM —
   * keep it to selectors the page owner has confirmed.
   */
  anchor?: string;
}

export interface EmbedContext {
  teaser: EmbedTeaser;
  subCopy: string;
  /** Composer placeholder. */
  placeholder: string;
  /**
   * Copy for the post-answer follow-up chips that the sample-question cards
   * were meant to move to (2026-08-15 empty-state redesign) — that surface
   * isn't built yet, and nothing reads this field today. Kept here so it
   * exists as real, page-specific copy the moment a chip surface lands
   * instead of being written from scratch then. Its `icon` currently has
   * **no renderer**: `PromptGlyph` was deleted from `embed-starter.tsx` when
   * the empty state stopped rendering prompt rows. A future chip surface
   * should either recover the glyphs from git history at `e55ac27` or drop
   * icons entirely.
   */
  prompts: readonly EmbedPrompt[];
  /**
   * Where the visitor came from, for the full page's "back to…" chip after
   * they tap expand ↗. Expand is a ROUND TRIP, not an exit (design 21c): a
   * paid visitor must never be stranded on the assistant with no route back
   * to the page that was trying to convert them.
   *
   * Absent on DEFAULT_EMBED_CONTEXT — with no resolved context there is no
   * honest destination to name, and a chip pointing somewhere we guessed is
   * worse than no chip.
   */
  returnUrl?: string;
  returnLabel?: string;
}

/**
 * Non-breaking space — "Part 1" must never wrap across a line break.
 *
 * Written as an escape, not the literal character. It WAS a literal U+00A0
 * and an automated edit silently replaced it with a plain space
 * (2026-08-15) — invisible, identical in every diff view, and invisible to
 * the tests, which only assert that the copy contains "Part". An escape
 * survives tooling that a raw non-breaking space does not.
 */
const NB = '\u00A0';

/**
 * The empty state's headline. A compile-time constant, not a per-context
 * field (decision 2026-08-15): one warm, generic greeting for every ctx, so
 * it names no page-specific noun. This also means it's the one piece of
 * empty-state copy that never waits on ctx resolution — the prerender shell
 * (embed-prerender-shell.tsx) imports it directly and paints it for real
 * instead of shimmering it.
 */
export const EMBED_GREETING = 'How can we help you?';

export const DEFAULT_EMBED_CONTEXT: EmbedContext = {
  // Generic teaser, no anchor: with no resolved context we don't know the
  // page, so dwell is the only honest trigger.
  teaser: {
    title: 'Have a question?',
    body: 'Courses, dates, what to expect.',
  },
  subCopy:
    'Ask any question about the course, the practice, or getting started.',
  placeholder: 'Ask anything…',
  prompts: [
    {
      label: 'Find a course near me',
      prompt: 'Help me find a course near me',
      icon: EMBED_PROMPT_ICON.CALENDAR,
    },
    {
      label: 'What is SKY Breath Meditation?',
      prompt: 'What is SKY Breath Meditation?',
      icon: EMBED_PROMPT_ICON.INFO,
    },
    {
      label: 'What do the courses cost?',
      prompt: 'What do the Art of Living courses cost?',
      icon: EMBED_PROMPT_ICON.PRICE,
    },
  ],
};

export const EMBED_CONTEXTS: Readonly<Record<string, EmbedContext>> = {
  // Art of Living Part 1 registration landing page — the first embed target.
  //
  // Copy rule (owner decision 2026-08-06): NEVER hardcode prices, dates, or
  // other volatile figures in this registry — a change on the landing page or
  // in course data becomes a silent mismatch here. Ask the question; let Sara
  // answer it from live data.
  'part1-lp': {
    teaser: {
      title: `Questions about Part${NB}1?`,
      body: 'I can find a date near you.',
      anchor: '#dates',
    },
    subCopy: `Ask any question about Part${NB}1 — dates near you, what the three days hold, what's included.`,
    placeholder: `Ask about Part${NB}1…`,
    returnUrl: 'https://event.us.artofliving.org/us-en/artoflivingpart1/',
    returnLabel: `Back to Part${NB}1 course`,
    prompts: [
      {
        label: `Find a Part${NB}1 date near me`,
        prompt: 'Find a Part 1 date near me',
        icon: EMBED_PROMPT_ICON.CALENDAR,
      },
      {
        label: 'What happens in the three days?',
        prompt: 'What happens in the three days of Part 1?',
        icon: EMBED_PROMPT_ICON.INFO,
      },
      {
        label: "What's included in the course fee?",
        prompt: "What's included in the Part 1 course fee?",
        icon: EMBED_PROMPT_ICON.PRICE,
      },
    ],
  },
};

/**
 * Cheap host-page identity signals the loader passes through on the iframe
 * URL (`p_ct`/`p_slug`/`p_path`/`p_lang` — see page-context-plan.md §5
 * WS-C.1 / D10: "Loader passes raw signals on the iframe URL; panel maps to
 * ctx" — an immutable loader must hold no ctx mapping of its own). Every
 * field is exactly what the loader could read cheaply at boot, before any
 * page-context extraction runs.
 */
export interface EmbedContextSignals {
  /** `body[data-analytics-p-course-type]`, e.g. `AOL_PART_1`. */
  courseType?: string | null;
  /** `html[data-wf-item-slug]` (Webflow) or the page's own slug. */
  slug?: string | null;
  /** `location.pathname`. */
  path?: string | null;
  lang?: string | null;
}

/**
 * `course_type` signal → ctx key. Deliberately tiny and hand-maintained: a
 * new course landing page earns a line here when it's actually built (and a
 * matching `EMBED_CONTEXTS` entry), never speculatively.
 */
const COURSE_TYPE_TO_CTX: Readonly<Record<string, string>> = {
  AOL_PART_1: 'part1-lp',
};

/**
 * Path/slug fallback when there's no explicit `ctx=` and no recognised
 * `course_type` — covers a host page that only ever exposes its own URL
 * (generic embeds, older loader versions). Substring match, not exact: real
 * slugs vary (`/artoflivingpart1/`, `/part1-lp/`, `/us-en/part1`, …).
 * Order matters — first match wins.
 */
const PATH_PATTERN_TO_CTX: ReadonlyArray<{ pattern: RegExp; ctxKey: string }> =
  [{ pattern: /part-?1/i, ctxKey: 'part1-lp' }];

/** Own-property lookup — see resolveEmbedContext's doc comment for why this
 *  is never a bare `EMBED_CONTEXTS[key]`. Returns null (not the default) so
 *  callers can tell "no such context" from "resolved to the default". */
function lookupCtx(key: string | null | undefined): EmbedContext | null {
  if (!key) return null;
  // Own-property check, NOT a bare `EMBED_CONTEXTS[key] ?? default`. `key`
  // comes straight off a URL query param, and a plain object literal
  // inherits from Object.prototype — so `?ctx=constructor` would resolve to
  // Object.prototype.constructor, a truthy Function that sails past `??` and
  // reaches the panel as a context with no subCopy. Same for `toString`,
  // `valueOf`, and friends. Every lookup below (explicit key, course_type
  // map, path pattern) goes through this same guard.
  if (!Object.hasOwn(EMBED_CONTEXTS, key)) return null;
  return EMBED_CONTEXTS[key];
}

/**
 * Resolve a host page's identity to its opener copy. Precedence (page-context-
 * plan.md D10): an explicit `?ctx=` always wins (an owner or the loader's
 * `data-ctx` attribute said exactly what this page is) → the `course_type`
 * signal, mapped through `COURSE_TYPE_TO_CTX` → a path/slug pattern match →
 * the generic default. Never guesses past a genuine miss — an unresolved
 * page falls all the way to `DEFAULT_EMBED_CONTEXT` rather than a
 * half-confident match.
 */
export function resolveEmbedContext(
  key: string | null | undefined,
  signals?: EmbedContextSignals
): EmbedContext {
  const explicit = lookupCtx(key);
  if (explicit) return explicit;

  const courseType = signals?.courseType;
  if (courseType && Object.hasOwn(COURSE_TYPE_TO_CTX, courseType)) {
    const byCourseType = lookupCtx(COURSE_TYPE_TO_CTX[courseType]);
    if (byCourseType) return byCourseType;
  }

  const haystack = `${signals?.slug ?? ''} ${signals?.path ?? ''}`;
  for (const { pattern, ctxKey } of PATH_PATTERN_TO_CTX) {
    if (!pattern.test(haystack)) continue;
    const byPath = lookupCtx(ctxKey);
    if (byPath) return byPath;
  }

  return DEFAULT_EMBED_CONTEXT;
}
